import React, { useState, useEffect } from 'react';
import {
    MonitorCog , SquareActivity , Tickets , Ticket, Clock, AlertCircle, CheckCircle,
    Eye, MessageCircle, Paperclip, Calendar,
    Filter, Search, RefreshCw, Download,
    ArrowRight, Hash, User, Mail, LogOut,
    ChevronRight, Home, Bell, Settings,
    Building, Menu, X
} from 'lucide-react';
import ApiService from '../services/api';

const MyTickets = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [user, setUser] = useState(null);
    const [filters, setFilters] = useState({
        status: 'all',
        priority: 'all',
        category: 'all',
        search: ''
    });
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        total: 0,
        limit: 15
    });

    useEffect(() => {
        fetchMyTickets();
        loadUser();

        // Time update effect
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, [filters, pagination.page]);

    const loadUser = () => {
        const userData = ApiService.getUserSync();
        setUser(userData);
    };

    const fetchMyTickets = async () => {
        try {
            setLoading(true);
            const params = {
                ...filters,
                page: pagination.page,
                limit: pagination.limit,
                sortBy: 'createdAt',
                sortOrder: 'desc'
            };

            // Remove 'all' values
            Object.keys(params).forEach(key => {
                if (params[key] === 'all') {
                    delete params[key];
                }
            });

            const response = await ApiService.getMyTickets(params);

            if (response.success) {
                setTickets(response.tickets || []);
                setPagination(prev => ({
                    ...prev,
                    pages: response.pagination?.pages || 1,
                    total: response.pagination?.total || 0
                }));
            }
        } catch (error) {
            console.error('Error fetching tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const getUserInitials = () => {
        if (!user) return 'U';
        const firstInitial = user.firstName ? user.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = user.lastName ? user.lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial || user.email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (!user) return 'User';
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.email;
    };

    const getStatusColor = (status) => {
        const colors = {
            'open': 'status-out-of-stock',
            'in-progress': 'status-low-stock',
            'resolved': 'status-available',
            'closed': 'status-available'
        };
        return `status-badge ${colors[status] || colors.open}`;
    };

    const getPriorityColor = (priority) => {
        const colors = {
            'low': '#059669',
            'medium': '#f59e0b',
            'high': '#ef4444',
            'critical': '#dc2626'
        };
        return colors[priority] || colors.medium;
    };

    const getStatusIcon = (status) => {
        const icons = {
            'open': <AlertCircle style={{ width: '14px', height: '14px' }} />,
            'in-progress': <Clock style={{ width: '14px', height: '14px' }} />,
            'resolved': <CheckCircle style={{ width: '14px', height: '14px' }} />,
            'closed': <CheckCircle style={{ width: '14px', height: '14px' }} />
        };
        return icons[status] || icons.open;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const calculateAge = (createdAt) => {
        const now = new Date();
        const created = new Date(createdAt);
        const diffMs = now - created;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffHours < 24) {
            return `${diffHours}h ago`;
        } else {
            return `${diffDays}d ago`;
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchMyTickets();
    };

    const handleViewTicket = (ticketId) => {
        window.open(`/support/ticket/${ticketId}`, '_blank');
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to log out?')) {
            ApiService.logout();
            window.location.href = '/login';
        }
    };

    const menuItems = [
        { id: 'technical-support', label: 'Submit Ticket', icon: Ticket, path: '/technical_support' },
        { id: 'my-tickets', label: 'My Tickets', icon: User, path: '/my-tickets', active: true },
        { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard' }
    ];

    if (loading) {
        return (
            <div className="dashboard-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                    <RefreshCw style={{ width: '32px', height: '32px', animation: 'spin 1s linear infinite', color: '#1e40af' }} />
                    <p style={{ marginTop: '16px', color: '#64748b' }}>Loading your tickets...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            {/* Sidebar */}
            <div className={`sidebar ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-logo-container">
                        <div className="sidebar-logo">
                            <Ticket style={{ width: '24px', height: '24px', color: 'white' }} />
                        </div>
                        {sidebarOpen && (
                            <div className="sidebar-brand">
                                <h1 className="sidebar-title">Support Portal</h1>
                                <p className="sidebar-subtitle">My Tickets</p>
                            </div>
                        )}
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item) => (
                        <a
                            key={item.id}
                            href={item.path}
                            className={`nav-item ${item.active ? 'active' : ''}`}
                        >
                            <item.icon style={{ width: '20px', height: '20px' }} />
                            {sidebarOpen && <span className="nav-text">{item.label}</span>}
                        </a>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <div className="user-profile">
                        <div className="user-avatar">{getUserInitials()}</div>
                        {sidebarOpen && (
                            <div className="user-info">
                                <p className="user-name">{getDisplayName()}</p>
                                <p className="user-role">{user?.role || 'User'}</p>
                            </div>
                        )}
                        <button
                            onClick={handleLogout}
                            className="logout-button"
                            title="Logout"
                        >
                            <LogOut style={{ width: '16px', height: '16px' }} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="main-content">
                {/* Header */}
                <div className="header">
                    <div className="header-content">
                        <div className="header-left">
                            <button
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className="menu-toggle"
                            >
                                {sidebarOpen ? <X style={{ width: '20px', height: '20px' }} /> : <Menu style={{ width: '20px', height: '20px' }} />}
                            </button>

                            <div className="breadcrumb">
                                <div className="breadcrumb-item">
                                    <Home style={{ width: '16px', height: '16px' }} />
                                    <span>Dashboard</span>
                                </div>
                                <ChevronRight style={{ width: '14px', height: '14px' }} />
                                <div className="breadcrumb-item">
                                    <Ticket style={{ width: '16px', height: '16px' }} />
                                    <span>Support</span>
                                </div>
                                <ChevronRight style={{ width: '14px', height: '14px' }} />
                                <div className="breadcrumb-item">
                                    <span style={{ color: '#1e40af', fontWeight: '600' }}>My Tickets</span>
                                </div>
                            </div>
                        </div>

                        <div className="header-right">
                            <div className="search-container">
                                <Search className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search tickets..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="search-input"
                                />
                            </div>

                            <div className="header-actions">
                                <button
                                    onClick={fetchMyTickets}
                                    className="header-button"
                                    title="Refresh"
                                >
                                    <RefreshCw style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button className="header-button" title="Notifications">
                                    <Bell style={{ width: '18px', height: '18px' }} />
                                </button>

                                <button className="header-button" title="Settings">
                                    <Settings style={{ width: '18px', height: '18px' }} />
                                </button>

                                <div className="time-display">
                                    {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="products-content">
                    {/* Page Header */}
                    <div className="page-header">
                        <h1 className="page-title">My Support Tickets</h1>
                        <p className="page-subtitle">Track and manage your support requests</p>
                        <div className="page-actions">
                            <button
                                onClick={() => window.location.href = '/technical_support'}
                                className="action-button"
                            >
                                <Ticket style={{ width: '20px', height: '20px' }} />
                                Submit New Ticket
                            </button>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="categories-section">
                        <h2 className="section-title">Overview</h2>
                        <div className="categories-grid">
                            <div className="category-card">
                                <div className="category-header">
                                    <div className="category-icon">
                                        <Ticket style={{ width: '20px', height: '20px', color: 'white' }} />
                                    </div>
                                    <div>
                                        <h3 className="category-name">Total Tickets</h3>
                                        <p className="category-count">{pagination.total}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="category-card">
                                <div className="category-header">
                                    <div className="category-icon" style={{ background: '#ef4444' }}>
                                        <AlertCircle style={{ width: '20px', height: '20px', color: 'white' }} />
                                    </div>
                                    <div>
                                        <h3 className="category-name">Open</h3>
                                        <p className="category-count" style={{ color: '#ef4444' }}>
                                            {tickets.filter(t => t.status === 'open').length}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="category-card">
                                <div className="category-header">
                                    <div className="category-icon" style={{ background: '#f59e0b' }}>
                                        <Clock style={{ width: '20px', height: '20px', color: 'white' }} />
                                    </div>
                                    <div>
                                        <h3 className="category-name">In Progress</h3>
                                        <p className="category-count" style={{ color: '#f59e0b' }}>
                                            {tickets.filter(t => t.status === 'in-progress').length}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="category-card">
                                <div className="category-header">
                                    <div className="category-icon" style={{ background: '#059669' }}>
                                        <CheckCircle style={{ width: '20px', height: '20px', color: 'white' }} />
                                    </div>
                                    <div>
                                        <h3 className="category-name">Resolved</h3>
                                        <p className="category-count" style={{ color: '#059669' }}>
                                            {tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="filters-section">
                        <div className="filters-row">
                            <div className="filter-group">
                                <label className="filter-label">Status</label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Status</option>
                                    <option value="open">Open</option>
                                    <option value="in-progress">In Progress</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="closed">Closed</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <label className="filter-label">Priority</label>
                                <select
                                    value={filters.priority}
                                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Priority</option>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <label className="filter-label">Search</label>
                                <input
                                    type="text"
                                    placeholder="Search tickets..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="filter-input"
                                />
                            </div>

                            <div className="filter-group">
                                <label className="filter-label">&nbsp;</label>
                                <button
                                    onClick={fetchMyTickets}
                                    className="action-button secondary"
                                    style={{ height: '36px' }}
                                >
                                    <RefreshCw style={{ width: '16px', height: '16px' }} />
                                    Refresh
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Tickets Table */}
                    <div className="products-table-section">
                        <div className="table-header">
                            <h2 className="table-title">Your Support Tickets</h2>
                            <div className="table-stats">{pagination.total} ticket{pagination.total !== 1 ? 's' : ''} found</div>
                        </div>

                        {tickets.length === 0 ? (
                            <div style={{ padding: '48px', textAlign: 'center' }}>
                                <Ticket style={{ width: '64px', height: '64px', color: '#d1d5db', margin: '0 auto 16px' }} />
                                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                                    No tickets found
                                </h3>
                                <p style={{ color: '#64748b', marginBottom: '24px' }}>
                                    You haven't submitted any support tickets yet.
                                </p>
                                <button
                                    onClick={() => window.location.href = '/technical_support'}
                                    className="action-button"
                                >
                                    <Ticket style={{ width: '20px', height: '20px' }} />
                                    Submit New Ticket
                                </button>
                            </div>
                        ) : (
                            <table className="products-table">
                                <thead className="table-head">
                                <tr>
                                    <th>Ticket ID</th>
                                    <th>Subject</th>
                                    <th>Status</th>
                                    <th>Priority</th>
                                    <th>Category</th>
                                    <th>Created</th>
                                    <th>Age</th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody className="table-body">
                                {tickets.map((ticket) => (
                                    <tr key={ticket._id}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Hash style={{ width: '12px', height: '12px', color: '#64748b' }} />
                                                <span className="product-name" style={{ fontFamily: 'monospace' }}>
                                                        {ticket.ticketId}
                                                    </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                <div className="product-name">{ticket.subject}</div>
                                                <div className="product-sku">
                                                    {ticket.description.substring(0, 60)}...
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                                <span className={getStatusColor(ticket.status)}>
                                                    {getStatusIcon(ticket.status)}
                                                    {ticket.status.replace('-', ' ').toUpperCase()}
                                                </span>
                                        </td>
                                        <td>
                                                <span style={{
                                                    color: getPriorityColor(ticket.priority),
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {ticket.priority}
                                                </span>
                                        </td>
                                        <td style={{ fontSize: '13px' }}>
                                            {ticket.category.replace('-', ' ')}
                                        </td>
                                        <td style={{ fontSize: '13px' }}>
                                            {formatDate(ticket.createdAt)}
                                        </td>
                                        <td>
                                                <span style={{
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    color: '#64748b'
                                                }}>
                                                    {calculateAge(ticket.createdAt)}
                                                </span>
                                        </td>
                                        <td>
                                            <div className="actions-cell">
                                                <button
                                                    className="action-icon"
                                                    onClick={() => handleViewTicket(ticket.ticketId)}
                                                    title="View Details"
                                                >
                                                    <Eye style={{ width: '16px', height: '16px' }} />
                                                </button>

                                                {ticket.responses && ticket.responses.length > 0 && (
                                                    <span style={{
                                                        fontSize: '12px',
                                                        color: '#1e40af',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                            <MessageCircle style={{ width: '12px', height: '12px' }} />
                                                        {ticket.responses.length}
                                                        </span>
                                                )}

                                                {ticket.attachments && ticket.attachments.length > 0 && (
                                                    <span style={{
                                                        fontSize: '12px',
                                                        color: '#64748b',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                            <Paperclip style={{ width: '12px', height: '12px' }} />
                                                        {ticket.attachments.length}
                                                        </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div style={{
                                padding: '20px 24px',
                                borderTop: '1px solid rgba(30, 64, 175, 0.1)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div className="table-stats">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} tickets
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                        className="action-button secondary"
                                        style={{ fontSize: '14px', padding: '8px 16px' }}
                                    >
                                        Previous
                                    </button>
                                    <span style={{
                                        padding: '8px 16px',
                                        background: '#1e40af',
                                        color: 'white',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600'
                                    }}>
                                        {pagination.page}
                                    </span>
                                    <button
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                        className="action-button secondary"
                                        style={{ fontSize: '14px', padding: '8px 16px' }}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyTickets;